import { useCallback, useEffect, useState, type ReactElement } from 'react';

/** Which ways a sideways list has more to show. */
export interface SideScrollRoom {
  back: boolean;
  forward: boolean;
}

const NO_ROOM: SideScrollRoom = { back: false, forward: false };

/**
 * A sideways list's room to move, and a way to move it a page at a time.
 *
 * A finger or a trackpad swipes a shelf; a mouse wheel only scrolls the page
 * down, and the shelves hide their scrollbars, so on a PC with a mouse the
 * games past the edge were out of reach. The buttons this drives are that
 * reach.
 *
 * Give the list `ref`. `content` is anything that changes when the list's
 * items do (their count), since new items can overflow a list whose own box
 * keeps its size. It starts with no room either way, so a server render (the
 * crawlable snapshot, which runs no script) draws no buttons that nothing
 * could press; in the browser it measures once the list is there, and again
 * on every scroll, resize and change of content.
 */
export function useSideScroll(content: unknown): {
  ref: (el: HTMLElement | null) => void;
  room: SideScrollRoom;
  page: (direction: 1 | -1) => void;
} {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [room, setRoom] = useState<SideScrollRoom>(NO_ROOM);

  useEffect(() => {
    // No list, no buttons: the shelf renders nothing without games.
    if (!el) return undefined;
    const measure = (): void => {
      const end = el.scrollWidth - el.clientWidth;
      // A pixel of slack: a scaled page can leave scrollLeft a fraction short.
      const next = { back: el.scrollLeft > 1, forward: el.scrollLeft < end - 1 };
      setRoom((prev) => (prev.back === next.back && prev.forward === next.forward ? prev : next));
    };
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const resized = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (resized) resized.observe(el);
    else window.addEventListener('resize', measure);
    return () => {
      el.removeEventListener('scroll', measure);
      if (resized) resized.disconnect();
      else window.removeEventListener('resize', measure);
    };
  }, [el, content]);

  const page = useCallback(
    (direction: 1 | -1) => {
      if (el) el.scrollBy({ left: direction * pageWidth(el), behavior: motion() });
    },
    [el],
  );

  return { ref: setEl, room, page };
}

/**
 * One page sideways: as many whole columns as are in view, so a press lands
 * on a column's edge — exactly where the shelf's scroll snapping would put it.
 */
export function pageWidth(el: HTMLElement): number {
  const style = getComputedStyle(el);
  const gap = parseFloat(style.columnGap) || 0;
  const view =
    el.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
  const first = el.firstElementChild;
  const column = first ? first.getBoundingClientRect().width + gap : 0;
  if (column <= 0) return Math.max(view, 1);
  return Math.max(1, Math.floor((view + gap) / column)) * column;
}

function motion(): ScrollBehavior {
  const reduced =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return reduced ? 'auto' : 'smooth';
}

/**
 * Back and forward for a sideways list, shown while it has more to show and
 * only where a mouse points (a finger swipes; the stylesheet hides them on
 * touch screens). `overlay` sets them on the list's edges, for the featured
 * cards, which have no heading row to sit in.
 */
export function SideScrollButtons({
  controls,
  label,
  room,
  onPage,
  overlay = false,
}: {
  /** The list's id. */
  controls: string;
  /** What the list is, e.g. "Flying games". */
  label: string;
  room: SideScrollRoom;
  onPage: (direction: 1 | -1) => void;
  overlay?: boolean;
}): ReactElement | null {
  if (!room.back && !room.forward) return null;
  return (
    <div className={overlay ? 'fg-scroll fg-scroll--overlay' : 'fg-scroll'}>
      <button
        type="button"
        className="fg-scroll__button"
        aria-controls={controls}
        aria-label={`Scroll back through ${label}`}
        disabled={!room.back}
        onClick={() => onPage(-1)}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path
            d="M10 3 5 8l5 5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <button
        type="button"
        className="fg-scroll__button"
        aria-controls={controls}
        aria-label={`Scroll forward through ${label}`}
        disabled={!room.forward}
        onClick={() => onPage(1)}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path
            d="m6 3 5 5-5 5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
}
